import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Shield, FileCheck, AlertTriangle, Clock, XCircle, Plus, User, CalendarDays, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLOR_CONFIG = {
  green: { label: 'Compliant', bg: 'bg-emerald-500', ring: 'ring-emerald-500/30', text: 'text-emerald-500', lightBg: 'bg-emerald-500/10' },
  amber: { label: 'Expiring Soon', bg: 'bg-amber-500', ring: 'ring-amber-500/30', text: 'text-amber-500', lightBg: 'bg-amber-500/10' },
  red: { label: 'Expired', bg: 'bg-red-500', ring: 'ring-red-500/30', text: 'text-red-500', lightBg: 'bg-red-500/10' },
  black: { label: 'Outstanding', bg: 'bg-zinc-700', ring: 'ring-zinc-500/30', text: 'text-zinc-400', lightBg: 'bg-zinc-500/10' },
};

const DOC_TYPES = ['DBS Check', 'Right to Work', 'Qualifications', 'Training Certificate', 'Insurance', 'Driving License', 'Reference', 'Health Declaration', 'First Aid Certificate', 'Safeguarding Certificate'];

export default function CaregiverCompliance() {
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCg, setExpandedCg] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ caregiver_id: '', document_type: 'DBS Check', title: '', expiry_date: '', renewal_date: '', notes: '' });
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchCompliance(); }, []);

  const fetchCompliance = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/caregiver-compliance`, { withCredentials: true });
      setCaregivers(data);
    } catch { toast.error('Failed to load compliance data'); }
    setLoading(false);
  };

  const handleAddDoc = async (e) => {
    e.preventDefault();
    if (!addForm.caregiver_id || !addForm.title) { toast.error('Select caregiver and enter title'); return; }
    setAdding(true);
    try {
      await axios.post(`${API_URL}/api/admin/caregiver-documents`, addForm, { withCredentials: true });
      toast.success('Document added');
      setShowAdd(false);
      setAddForm({ caregiver_id: '', document_type: 'DBS Check', title: '', expiry_date: '', renewal_date: '', notes: '' });
      fetchCompliance();
    } catch { toast.error('Failed to add document'); }
    setAdding(false);
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/caregiver-documents/${docId}`, { withCredentials: true });
      toast.success('Document deleted');
      fetchCompliance();
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = filter === 'all' ? caregivers : caregivers.filter(c => c.compliance_color === filter);
  const colorCounts = { green: 0, amber: 0, red: 0, black: 0 };
  caregivers.forEach(c => { if (colorCounts[c.compliance_color] !== undefined) colorCounts[c.compliance_color]++; });

  if (loading) return (
    <DashboardLayout><div className="flex justify-center py-20"><div className="spinner w-8 h-8" /></div></DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="caregiver-compliance-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">Document Compliance</h1>
            <p className="text-muted-foreground mt-1">Track caregiver document status with color-coded compliance</p>
          </div>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <Button className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" data-testid="add-compliance-doc-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Document
              </Button>
            </DialogTrigger>
            <DialogContent className="glass-card border-border sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-foreground">Add Caregiver Document</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddDoc} className="space-y-4">
                <div>
                  <Label className="text-foreground">Caregiver *</Label>
                  <Select value={addForm.caregiver_id} onValueChange={v => setAddForm(f => ({ ...f, caregiver_id: v }))}>
                    <SelectTrigger className="mt-1 bg-muted border-border text-foreground" data-testid="compliance-caregiver-select">
                      <SelectValue placeholder="Select caregiver" />
                    </SelectTrigger>
                    <SelectContent>
                      {caregivers.map(cg => <SelectItem key={cg.id} value={cg.id}>{cg.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-foreground">Document Type</Label>
                    <Select value={addForm.document_type} onValueChange={v => setAddForm(f => ({ ...f, document_type: v }))}>
                      <SelectTrigger className="mt-1 bg-muted border-border text-foreground" data-testid="compliance-doc-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-foreground">Title *</Label>
                    <Input value={addForm.title} onChange={e => setAddForm(f => ({ ...f, title: e.target.value }))} className="mt-1 bg-muted border-border text-foreground" data-testid="compliance-doc-title" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-foreground">Expiry Date</Label>
                    <Input type="date" value={addForm.expiry_date} onChange={e => setAddForm(f => ({ ...f, expiry_date: e.target.value }))} className="mt-1 bg-muted border-border text-foreground" data-testid="compliance-expiry-date" />
                  </div>
                  <div>
                    <Label className="text-foreground">Renewal Date</Label>
                    <Input type="date" value={addForm.renewal_date} onChange={e => setAddForm(f => ({ ...f, renewal_date: e.target.value }))} className="mt-1 bg-muted border-border text-foreground" data-testid="compliance-renewal-date" />
                  </div>
                </div>
                <div>
                  <Label className="text-foreground">Notes</Label>
                  <textarea value={addForm.notes} onChange={e => setAddForm(f => ({ ...f, notes: e.target.value }))} className="mt-1 w-full rounded-md bg-muted border border-border text-foreground p-3 text-sm min-h-[60px] resize-none" data-testid="compliance-doc-notes" />
                </div>
                <Button type="submit" className="w-full bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" disabled={adding} data-testid="save-compliance-doc-btn">
                  {adding ? 'Saving...' : 'Add Document'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Color-Coded Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(COLOR_CONFIG).map(([color, config]) => (
            <Card
              key={color}
              className={`border cursor-pointer transition-all ${filter === color ? `${config.ring} ring-2` : 'border-border'}`}
              onClick={() => setFilter(filter === color ? 'all' : color)}
              data-testid={`compliance-filter-${color}`}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-4 h-4 rounded-full ${config.bg}`} />
                <div>
                  <p className="text-xs text-muted-foreground">{config.label}</p>
                  <p className={`text-xl font-bold ${config.text}`}>{colorCounts[color]}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Caregiver List */}
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <Card className="glass-card border-border">
              <CardContent className="p-12 text-center">
                <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-30" />
                <p className="text-muted-foreground">No caregivers found</p>
              </CardContent>
            </Card>
          ) : filtered.map(cg => {
            const config = COLOR_CONFIG[cg.compliance_color] || COLOR_CONFIG.black;
            const isExpanded = expandedCg === cg.id;
            return (
              <Card key={cg.id} className={`glass-card border-border transition-all ${isExpanded ? `${config.ring} ring-1` : ''}`} data-testid={`compliance-cg-${cg.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4 cursor-pointer" onClick={() => setExpandedCg(isExpanded ? null : cg.id)}>
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${config.bg}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{cg.name}</p>
                      <p className="text-xs text-muted-foreground">{cg.email}</p>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className={`px-2 py-1 rounded-full ${config.lightBg} ${config.text} font-medium`}>{config.label}</span>
                      <span className="text-muted-foreground">{cg.stats.total} doc(s)</span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-border space-y-2 animate-in slide-in-from-top-1">
                      <div className="flex gap-4 text-xs text-muted-foreground mb-3">
                        <span className="text-emerald-500">Valid: {cg.stats.valid}</span>
                        <span className="text-amber-500">Expiring: {cg.stats.expiring_soon}</span>
                        <span className="text-red-500">Expired: {cg.stats.expired}</span>
                        <span className="text-zinc-400">Outstanding: {cg.stats.outstanding}</span>
                      </div>
                      {cg.documents.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-3">No documents uploaded</p>
                      ) : cg.documents.map(doc => {
                        const docColor = COLOR_CONFIG[doc.compliance_status] || COLOR_CONFIG.black;
                        return (
                          <div key={doc.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30" data-testid={`compliance-doc-${doc.id}`}>
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${docColor.bg}`} />
                            <FileCheck className={`w-4 h-4 flex-shrink-0 ${docColor.text}`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-foreground">{doc.title}</p>
                              <p className="text-xs text-muted-foreground">{doc.document_type} {doc.expiry_date ? `· Expires: ${doc.expiry_date}` : '· No expiry set'}</p>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${docColor.lightBg} ${docColor.text}`}>{docColor.label}</span>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-500/10 h-7 w-7 p-0" onClick={() => handleDeleteDoc(doc.id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
